import { ChangeDetectionStrategy, Component, effect, inject, signal, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ScoringDefinitionInterface } from '@vendasta/crm';
import { FIELD_ID_URL_PARAM, OBJECT_TYPE_URL_PARAM } from '../../constants';
import { CrmFieldService, CrmInjectionToken, ObjectType, StandardExternalIds } from '@galaxy/crm/static';
import { catchError, firstValueFrom, of, switchMap, take } from 'rxjs';
import { ScoringDefinitionService } from '../scoring-definition.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { isAcceptedObjectType, validateScoringDefinitions } from '../../validators';
import { ScoringDefinitionSectionComponent } from '../section/section.component';
import {
  DefaultCompanyScoringDefinitions,
  getDefaultContactScoringDefinitions,
} from '../../default-scoring-definition';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

export type Type = 'positive' | 'negative';

@Component({
  selector: 'crm-scoring-definition-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScoringDefinitionPageComponent {
  @ViewChildren(ScoringDefinitionSectionComponent) sections?: ScoringDefinitionSectionComponent[];

  private readonly config = inject(CrmInjectionToken);
  private readonly scoringDefinitionService = inject(ScoringDefinitionService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbarService = inject(SnackbarService);
  private readonly iamService = inject(IAMService);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly crmFieldService = inject(CrmFieldService);

  protected modified = false;

  readonly fieldId = signal<string>('');
  readonly objectType = signal<ObjectType | undefined>(undefined);
  readonly isLoading = signal<boolean>(false);
  readonly scoringDefinitions = signal<ScoringDefinitionInterface[]>([]);
  readonly positiveScoringDefinitions = signal<ScoringDefinitionInterface[]>([]);
  readonly negativeScoringDefinitions = signal<ScoringDefinitionInterface[]>([]);
  readonly isMobile$ = this.scoringDefinitionService.isMobile$;
  readonly showEmptyState = signal<boolean>(false);

  constructor() {
    this.setupRouteWatcher();
    this.setupScoreDefinitionsWatcher();
  }

  async handleSave(): Promise<void> {
    if (this.isValid()) {
      return this.saveScoringDefinitions();
    }
  }

  async handlePositiveScoringDefinitionsChange(scoringDefinitions: ScoringDefinitionInterface[]): Promise<void> {
    this.positiveScoringDefinitions.set(scoringDefinitions);
  }

  async handleNegativeScoringDefinitionsChange(scoringDefinitions: ScoringDefinitionInterface[]): Promise<void> {
    this.negativeScoringDefinitions.set(scoringDefinitions);
  }

  private setupRouteWatcher(): void {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.fieldId.set(params.get(FIELD_ID_URL_PARAM) ?? '');
      const objectType = params.get(OBJECT_TYPE_URL_PARAM);
      if (isAcceptedObjectType(objectType)) {
        this.objectType.set(objectType);
      } else {
        this.objectType.set(undefined);
      }
    });
    effect(
      () => {
        this.loadScoringDefinition(this.fieldId());
      },
      { allowSignalWrites: true },
    );
  }

  private setupScoreDefinitionsWatcher(): void {
    effect(
      () => {
        const definitions = this.scoringDefinitions();
        this.positiveScoringDefinitions.set(definitions.filter((d) => d.contribution! >= 0));
        this.negativeScoringDefinitions.set(definitions.filter((d) => d.contribution! < 0));
      },
      { allowSignalWrites: true },
    );
  }

  private async loadScoringDefinition(fieldId: string): Promise<void> {
    if (fieldId) {
      this.isLoading.set(true);
      try {
        const namespace = await firstValueFrom(this.config.namespace$);
        const response = await firstValueFrom(this.scoringDefinitionService.listScoringDefinitions(namespace, fieldId));
        this.showEmptyState.set(!response.length);
        this.scoringDefinitions.set(response);
      } finally {
        this.isLoading.set(false);
        this.reset();
      }
    }
  }

  private buildScoringDefinitions(): ScoringDefinitionInterface[] {
    const positive = this.positiveScoringDefinitions();
    const negative = this.negativeScoringDefinitions();
    return [...positive, ...negative];
  }

  private isValid(): boolean {
    try {
      validateScoringDefinitions(this.positiveScoringDefinitions(), this.negativeScoringDefinitions());
      return true;
    } catch (e) {
      const msg = (e as Error).message;
      this.snackbarService.openErrorSnack(msg);
      return false;
    }
  }

  private async saveScoringDefinitions(): Promise<void> {
    this.isLoading.set(true);
    try {
      this.productAnalyticsService.trackEvent('crm-update-scoring-definitions', 'scoring-definitions', 'click', 1);
      await this.trackEventInChameleon();
      const namespace = await firstValueFrom(this.config.namespace$);
      const scoringDefinitions = this.buildScoringDefinitions();
      const result = await firstValueFrom(
        this.scoringDefinitionService.updateScoringDefinitions(namespace, this.fieldId(), scoringDefinitions),
      );
      if (!result) {
        throw new Error('Failed to update scoring definitions');
      }
      this.reset();
      this.snackbarService.openSuccessSnack('SCORE_SETTINGS.SUCCESS_MESSAGES.UPDATE');
    } catch {
      this.snackbarService.openErrorSnack('SCORE_SETTINGS.ERROR.FAILED_TO_UPDATE');
    } finally {
      this.isLoading.set(false);
    }
  }

  private reset(): void {
    this.sections?.forEach((section) => section.reset());
    this.modified = false;
  }

  protected addCriteria(type: Type): void {
    const scoringDefinition = type === 'positive' ? this.positiveScoringDefinitions : this.negativeScoringDefinitions;
    const newDefinition: ScoringDefinitionInterface = {
      contribution: type === 'positive' ? 1 : -1,
      crmObjectType: this.objectType(),
      fieldId: this.fieldId(),
      scoringRules: [
        {
          crmObjectType: this.objectType(),
          objectRules: [{ filter: { fieldId: '', values: [] } }],
        },
      ],
    };
    const currentDefinitions = scoringDefinition();
    scoringDefinition.set([...currentDefinitions, newDefinition]);
    this.modified = true;
  }

  protected handleSectionModified(): void {
    if (!this.modified) {
      this.modified = true;
    }
  }

  protected populateWithDefaultScoringDefinitions(): void {
    switch (this.objectType()) {
      case 'Company':
        this.scoringDefinitions.set(DefaultCompanyScoringDefinitions);
        break;
      case 'Contact':
        this.scoringDefinitions.set(
          getDefaultContactScoringDefinitions(
            this.crmFieldService.getFieldId(StandardExternalIds.Email),
            this.crmFieldService.getFieldId(StandardExternalIds.PhoneNumber),
          ),
        );
        break;
      default:
        return;
    }
    this.hideEmptyState();
    this.modified = true;
  }

  protected hideEmptyState(): void {
    this.showEmptyState.set(false);
  }

  private async trackEventInChameleon(): Promise<void> {
    const chmln = window['chmln'];
    if (!chmln) {
      return;
    }
    this.config.currentUserId$
      .pipe(
        switchMap((userId) => {
          return this.iamService.getUser(new UserIdentifier({ userId: userId }));
        }),
        catchError(() => of(null)),
        take(1),
      )
      .subscribe((user) => {
        if (user) {
          chmln.identify(user.userId, {
            email: user.email,
            name: user.firstName + ' ' + user.lastName,
          });
          chmln.track('Updated scoring definitions');
        }
      });
  }
}
